// Demo data for the /features/memberships page: the same shapes the API sends
// the real membership components (MembershipTypeRow, MembershipRow,
// MembershipGateCard, MeetPassOffer, MembershipTypesTable), filled with a
// made-up club, so the page runs the real thing on static props and never
// calls the API. Nothing here is a real club, player or membership.
import {COVERS} from '~/utils/membershipGroups';
import {DISPLAY_AS, TYPE_STATUS, TYPE_VISIBILITY} from '~/utils/memberships';

export const MEMBERSHIP_DEMO_CLUB = Object.freeze({id: '00000000-0000-0000-0000-000000000001', name: 'Riverside Racquets Club'});

const id = (n) => `00000000-0000-0000-0000-${String(n).padStart(12, '0')}`;
const clubId = MEMBERSHIP_DEMO_CLUB.id;

const plusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
const dayWord = (days) => new Date(plusDays(days) + 'T12:00:00').toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});

/** The club page's list: what a player sees under Memberships & passes (MembershipTypeRow's `type`). */
export const membershipDemoTypes = Object.freeze([
  {
    id: id(101), clubId, name: 'Senior membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson,
    priceSummary: '£240.00 a year, or £22.00 a month', lengthSummary: 'Renews every year', whoCanJoin: '18 and over', requiresApproval: false,
  },
  {
    id: id(102), clubId, name: 'Junior membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson,
    priceSummary: '£90.00 a year', lengthSummary: 'Renews every year', whoCanJoin: 'Under 18 on 31 August', requiresApproval: true,
    conditions: [{text: 'I have read the junior code of conduct.'}],
  },
  {
    id: id(103), clubId, name: 'Family membership', displayAs: DISPLAY_AS.membership, covers: COVERS.group,
    priceSummary: '£420.00 a year', lengthSummary: 'Renews every year', whoCanJoin: 'Anyone', requiresApproval: false,
    whoItCovers: 'For 1 to 2 adults (18 and over) and up to 4 children (under 18)',
  },
  {
    id: id(104), clubId, name: 'Introductory membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson,
    priceSummary: '£60.00 for 3 months', lengthSummary: 'Then renews as Senior', whoCanJoin: '18 and over', requiresApproval: false,
    newToClubLine: 'For people new to the club', renewsAsLine: 'Renews as Senior membership',
  },
  {
    id: id(105), clubId, name: 'Summer pass', displayAs: DISPLAY_AS.pass, covers: COVERS.onePerson,
    priceSummary: '£60.00 for the pass', lengthSummary: '12 weeks from 1 June', whoCanJoin: 'Anyone', requiresApproval: false,
  },
  {
    id: id(106), clubId, name: 'Wednesday Mixer pass', displayAs: DISPLAY_AS.pass, covers: COVERS.onePerson,
    priceSummary: '£40.00 for the pass', lengthSummary: '8 sessions', whoCanJoin: 'Anyone', requiresApproval: false,
  },
]);

/** Profile → Memberships & passes → Yours (MembershipRow's `membership`). */
export const membershipDemoMine = Object.freeze([
  {
    id: id(201), membershipTypeId: id(101), membershipTypeName: 'Senior membership', displayAs: DISPLAY_AS.membership, clubName: MEMBERSHIP_DEMO_CLUB.name,
    status: 'Active', cancelAtPeriodEnd: false, renewsOrEndsLine: `Renews ${dayWord(212)} · £22.00 a month`, datesLine: `Renews ${dayWord(212)}`, priceLine: '£22.00 a month',
  },
  {
    id: id(202), membershipTypeId: id(106), membershipTypeName: 'Wednesday Mixer pass', displayAs: DISPLAY_AS.pass, clubName: MEMBERSHIP_DEMO_CLUB.name,
    status: 'Active', cancelAtPeriodEnd: false, renewsOrEndsLine: `Ends ${dayWord(41)} · 5 sessions left`, datesLine: `This block ends ${dayWord(41)} · 5 sessions left`, priceLine: '£40.00 for the pass',
  },
  {
    id: id(203), membershipTypeId: id(903), membershipTypeName: 'Family membership', displayAs: DISPLAY_AS.membership, clubName: 'Hillside Tennis Club',
    status: 'Active', cancelAtPeriodEnd: true, groupLine: 'Carter family · 4 people', renewsOrEndsLine: `Ends ${dayWord(96)}`, datesLine: `Ends ${dayWord(96)}`, priceLine: '£420.00 a year, for the group', payerName: 'Jo Carter',
  },
]);

/** "Membership or pass needed" on a competition's invite page (MembershipGateCard's `rule`). */
export const membershipDemoGate = Object.freeze({
  eligibility: 'Restricted',
  holdsCurrent: false,
  neededLine: 'Senior or Junior membership needed',
  priceLine: 'Senior and Junior members enter for free. Otherwise £5.00.',
  qualifyingTypes: [membershipDemoTypes[0], membershipDemoTypes[1]],
  pendingApplicationId: null,
});

/** The pass offered beside Count me in on a meet (MeetPassOffer's `offer`). */
export const membershipDemoPassOffer = Object.freeze({
  clubId, typeId: id(106), typeName: 'Wednesday Mixer pass', currency: 'gbp',
  priceNowMinor: 4000, perSessionMinor: 800, sessionsLeft: 8, thenLine: 'Then £40.00 for each block of 8 sessions.',
});

/** Memberships & passes → What you offer (MembershipTypesTable's `types`). */
export const membershipDemoAdminTypes = Object.freeze([
  {
    id: id(101), name: 'Senior membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson, status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£240.00 a year, or £22.00 a month', lengthSummary: 'Renews every year', whoCanJoin: '18 and over', currentCount: 148, pendingApplicationCount: 0,
  },
  {
    id: id(102), name: 'Junior membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson, status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£90.00 a year', lengthSummary: 'Renews every year', whoCanJoin: 'Under 18 on 31 August', currentCount: 63, pendingApplicationCount: 1,
  },
  {
    id: id(103), name: 'Family membership', displayAs: DISPLAY_AS.membership, covers: COVERS.group, status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£420.00 a year', lengthSummary: 'Renews every year', whoItCovers: 'For 1 to 2 adults and up to 4 children', currentCount: 41, pendingApplicationCount: 1,
  },
  {
    id: id(104), name: 'Introductory membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson, status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.hidden,
    priceSummary: '£60.00 for 3 months', lengthSummary: 'Renews as Senior', whoCanJoin: '18 and over', newToClubOnly: true, currentCount: 9, pendingApplicationCount: 0,
  },
  {
    id: id(105), name: 'Summer pass', displayAs: DISPLAY_AS.pass, covers: COVERS.onePerson, status: TYPE_STATUS.closedToNew, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£60.00 for the pass', lengthSummary: '12 weeks from 1 June', whoCanJoin: 'Anyone', currentCount: 27, pendingApplicationCount: 0,
  },
  {
    id: id(106), name: 'Wednesday Mixer pass', displayAs: DISPLAY_AS.pass, covers: COVERS.onePerson, status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£40.00 for the pass', lengthSummary: 'Repeating 8-session blocks', whoCanJoin: 'Anyone', currentCount: 22, pendingApplicationCount: 1,
  },
  {
    id: id(107), name: 'Student membership', displayAs: DISPLAY_AS.membership, covers: COVERS.onePerson, status: TYPE_STATUS.draft, visibility: TYPE_VISIBILITY.public,
    priceSummary: '£120.00 a year', lengthSummary: 'Renews every year', whoCanJoin: '18 to 25', currentCount: 0, pendingApplicationCount: 0,
  },
]);

/** The applications queue, as the Applications page lists it. */
export const membershipDemoApplications = Object.freeze([
  {id: id(301), name: 'Priya Khan', type: 'Junior membership', price: '£90.00', line: 'parent or carer agreed · Health answered', when: 'today', hold: '6 days left'},
  {id: id(302), name: 'The Morgan family', type: 'Family membership', price: '£420.00', line: 'Group · 4 people', when: '1 day ago', hold: '5 days left'},
  {id: id(303), name: 'Sam Taylor', type: 'Wednesday Mixer pass', price: '£40.00', line: 'Pass', when: '3 days ago', hold: '3 days left'},
]);
